import {ChangeDetectorRef, Component, OnInit, input, computed, signal, OnDestroy} from '@angular/core';
import {DefaultReactiveComponent} from '../default-reactive/default-reactive.component';
import {FormControl, FormGroup} from '@angular/forms';
import {
    statusCategoryStart,
    statusCategoryOngoing,
    statusCategoryFinal,
    campaignStatusListentryListName,
} from './campaigns-config';
import {Campaign, CampaignInstitution, CampaignPerson, CampaignStep} from '@shared/campaign';
import {Listentry} from '@shared/listentry';
import {safeDate, formatStandardDate, isDateWithinDays} from '@shared/utils/datetime';
import {InstitutionsService} from '@modules/institutions/institutions.service';
import {PeopleService} from '@modules/people/people.service';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {CampaignsService} from './campaigns.service';
import {CampaignTarget, getCampaignTargets} from './campaign-target-type';
import {Id} from '@shared/types';
import {notEmpty} from '@shared/utils';
import {ListentriesService} from '@global/services/listentries.service';
import {distinctUntilChanged, Subject, switchMap, takeUntil} from 'rxjs';
import {InitService} from '@global/services/init.service';
import {User} from '@shared/user';
import {StorageService} from '@global/services/storage.service';

@Component({
    selector: 'phscw-campaigns',
    templateUrl: './campaigns.component.html',
    styleUrls: ['./campaigns.component.scss'],
    standalone: false,
})
export class CampaignsComponent extends DefaultReactiveComponent implements OnInit, OnDestroy {
    parentModule = input.required<'people' | 'institutions'>();

    // Selected institution or person observables
    institutionId = toSignal(this.institutionsService.selectionChanged.asObservable());
    personId = toSignal(this.peopleService.selectionChanged.asObservable());

    // Selected entity id (person or institution, depending on the parent module)
    selectedEntityId = computed(() =>
        this.parentModule() === 'institutions' ? this.institutionId() : this.personId(),
    );

    /*
     * Set of hidden campaign ids per entity id (institution/person)
     * Used to hide campaigns that have reached their final step and status without needing to reload from backend
     */
    protected readonly hiddenByEntity = signal<Map<number, Set<number>>>(new Map());

    withMatchingRoles = input<boolean>(true);

    /**
     * Campaigns from backend
     * - "ignoreRoles": true -> all campaigns
     * - "ignoreRoles": false -> only campaigns that match the user role
     */
    private readonly campaignsSource = toSignal(
        toObservable(this.withMatchingRoles).pipe(
            distinctUntilChanged(),
            // Switch between the two shared service streams (with or without role filtering)
            switchMap((wmr) => this.campaignsService.loadCampaigns(!wmr)),
        ),
        {initialValue: [] as Campaign[]},
    );

    /**
     * List of campaigns
     * - Predicate: campaign has any target with entity_id === selectedId
     * - Map: assign filtered targets to campaigns_institutions or campaigns_people
     */
    private filteredCampaigns = computed<Campaign[]>(() => {
        // Only get the campaigns that are visible for the user
        const all = this.campaignsSource();

        // Currently selected institution/person id
        const selectedId = this.selectedEntityId();

        // 'institutions' or 'people' module
        const scope = this.parentModule();

        // Hidden campaigns for the selected entity
        const hiddenMap = this.hiddenByEntity();

        if (!selectedId || !Array.isArray(all)) return [];

        const hiddenSet = hiddenMap.get(selectedId) ?? new Set<number>();

        // Return campaigns that match the selected institution/person and are not hidden
        return all
            .filter((c) => getCampaignTargets(c, scope).some((t) => t.entity_id === selectedId))
            .map((c) => {
                const targets = getCampaignTargets(c, scope).filter((t) => t.entity_id === selectedId);
                return scope === 'institutions' ?
                    {...c, campaigns_institutions: targets as CampaignInstitution[]} :
                    {...c, campaigns_people: targets as CampaignPerson[]};
            })
            .filter((c) => !hiddenSet.has(c.id!));
    });

    // Use the filteredCampaigns signal as an observable for compability with the old code from PharmaPilot
    public campaignsList$ = toObservable(this.filteredCampaigns);

    // Shared utility functions that are used in the HTML Template
    public safeDate = safeDate;
    public formatStandardDate = formatStandardDate;
    public isDateWithinDays = isDateWithinDays;
    public Number = Number;

    // Status categories for the 'CampaignStatus' listentry
    public readonly STATUS_CATEGORY_START = statusCategoryStart;
    public readonly STATUS_CATEGORY_ONGOING = statusCategoryOngoing;
    public readonly STATUS_CATEGORY_FINAL = statusCategoryFinal;

    // FormGroup to track the status select box for value changes
    public campaignStatusFormGroups: FormGroup = new FormGroup({});

    // Listentries for campaignStatus to match the correct status_key
    public campaignStatusListentries: Listentry[] | undefined = [];
    private campaignStatusListName = campaignStatusListentryListName;

    // States for Animation for all campaigns
    public campaignRemoveAnimationStates = new Map<number, boolean>();
    public campaignFlipAnimationStates = new Map<number, boolean>();

    // to reset the form group
    readonly #formGroupReset$ = new Subject<void>();

    currentUser: User | null = null;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private institutionsService: InstitutionsService,
        private peopleService: PeopleService,
        private campaignsService: CampaignsService,
        private listentriesService: ListentriesService,
        private initService: InitService,
        private storageService: StorageService,
    ) {
        super();
    }

    ngOnInit() {
        this.subscribeUntilDestroyed(this.initService.allInitialized, (result) => {
            if (result) {
                this.loadListentries();
                this.loadUserData().then(() => {
                    this.initializeCampaignStatusFormGroups();
                });
            }
        });

        // Leider doppelt notwendig wegen timing / refresh Problemen, TODO: fix
        this.loadListentries();
        this.loadUserData().then(() => {
            this.initializeCampaignStatusFormGroups();
        });
    }

    override ngOnDestroy(): void {
        this.#formGroupReset$.next();
        this.#formGroupReset$.complete();
        super.ngOnDestroy();
    }

    private loadListentries() {
        // Campaign Status
        this.listentriesService.loadList(this.campaignStatusListName).then((value) => {
            this.campaignStatusListentries = value;
        });
    }

    /**
     * Create FormGroups for every campaign of the selected institution/person.
     * @description
     * This is used to save changed statuses.
     * Each campaign will have a separate event listener to react on status changes.
     */
    private initializeCampaignStatusFormGroups(): void {
        this.subscribeUntilDestroyed(this.campaignsList$, (campaignList) => {
            // complete old subscriptions before rebuilding to avoid duplicates
            this.#formGroupReset$.next();
            this.campaignStatusFormGroups = new FormGroup({});

            for (const campaign of campaignList) {
                const campaignId = String(campaign.id);
                const target = this.getLatestCampaignTargetByCampaignStep(campaign);
                if (!target) continue;

                const singleCampaignStatusForm = new FormGroup({
                    campaign_id: new FormControl(target.campaign_id),
                    campaign_step_id: new FormControl(target.campaign_step_id),
                    status_key: new FormControl(target.status_key),
                    value_string: new FormControl(target.value_string),
                    ...(target.target_type === 'institution' ?
                        {institution_id: new FormControl(target.entity_id)} :
                        {person_id: new FormControl(target.entity_id)}),
                });

                // Disable the select box if the user has no edit permission for this campaign
                if (!this.hasEditPermission(campaign)) {
                    singleCampaignStatusForm.get('status_key')?.disable({emitEvent: false});
                }

                this.campaignStatusFormGroups.addControl(campaignId, singleCampaignStatusForm);

                // Subscribe to status changes
                this.campaignStatusFormGroups
                    .get(campaignId)!
                    .valueChanges.pipe(
                        takeUntil(this.#formGroupReset$), // completes previous subscriptions
                        takeUntil(this.componentDestroyed$),
                    )
                    .subscribe((updatedTarget) => this.updateCampaignStatus(updatedTarget as CampaignTarget));

                this.campaignRemoveAnimationStates.set(Number(campaignId), false);
                this.campaignFlipAnimationStates.set(Number(campaignId), false);
            }

            this.changeDetectorRef.markForCheck();
        });
    }

    /**
     * Update Campaign Status
     * @param {CampaignTarget} target campaigns_institutions or campaigns_people
     */
    private updateCampaignStatus(target: CampaignTarget) {
        const campaign = {
            id: target.campaign_id,
            ...(this.parentModule() === 'institutions' ?
                {campaigns_institutions: [target as CampaignInstitution]} :
                {campaigns_people: [target as CampaignPerson]}),
        };

        if (this.isFinalStatus(target.status_key)) {
            const nextStepId = this.getNextCampaignStepId(target);

            if (nextStepId) {
                // FLIP CASE: prepare next step, do NOT hide
                const nextEntry = {
                    campaign_id: target.campaign_id,
                    campaign_step_id: nextStepId,
                    status_key: this.getInitialStatusKey(),
                    value_string: null,
                    ...(this.parentModule() === 'institutions' ?
                        {institution_id: (target as CampaignInstitution).institution_id} :
                        {person_id: (target as CampaignPerson).person_id}),
                };

                if (this.parentModule() === 'institutions') {
                    // push to campaigns_institutions
                    (
                        campaign as {id: number; campaigns_institutions: CampaignInstitution[]}
                    ).campaigns_institutions?.push(nextEntry as CampaignInstitution);
                } else {
                    // push to campaigns_people
                    (campaign as {id: number; campaigns_people: CampaignPerson[]}).campaigns_people?.push(
                        nextEntry as CampaignPerson,
                    );
                }

                this.setCampaignAnimationState(target.campaign_id, 'flip', true);

                setTimeout(() => {
                    // reload on success
                    this.updateOrSaveCampaign(campaign, true);
                    this.setCampaignAnimationState(target.campaign_id, 'flip', false);
                }, 600);
            } else {
                // REMOVE CASE: last step -> animate out and hide
                this.setCampaignAnimationState(target.campaign_id, 'remove', true);

                setTimeout(() => {
                    const cid = Number(target.campaign_id);
                    // Hide campaign for current entity
                    this.hideForCurrentEntity(cid);
                    this.updateOrSaveCampaign(campaign, false, cid);
                    this.setCampaignAnimationState(target.campaign_id, 'remove', false);
                }, 600);
            }
        } else {
            // No animation
            this.updateOrSaveCampaign(campaign, false);
        }
    }

    /**
     * Get the campaigns_institutions or campaigns_people entry with the latest step
     * @param {Campaign} campaign
     */
    private getLatestCampaignTargetByCampaignStep(campaign: Campaign): CampaignTarget | undefined {
        const latestStep = this.getLatestCampaignStep(campaign);

        return getCampaignTargets(campaign, this.parentModule()).find(
            (target) => target.campaign_step_id === latestStep?.id,
        );
    }

    /**
     * Get the latest step for the given campaign
     * @param {Campaign} campaign
     */
    public getLatestCampaignStep(campaign: Campaign): CampaignStep | undefined {
        const campaignSteps = campaign.campaign_steps;
        const targets = getCampaignTargets(campaign, this.parentModule());

        // Get any campaign institution/person to start with
        const initialTarget = targets[0];
        if (!initialTarget) {
            return undefined;
        }

        // Get the current step
        let currentStep = campaignSteps.find((step) => step.id === initialTarget.campaign_step_id);

        while (currentStep?.next_step_id !== null) {
            // Find the next possible step
            const nextStepId: Id = currentStep!.next_step_id;
            const nextStep = campaignSteps.find((step) => step.id === nextStepId);

            // Stop if no next step exist
            if (!nextStep) {
                break;
            }

            // Find the next CampaignInstitution/CampaignPerson entry with the found step
            const hasNextTarget = targets.some((t) => t.campaign_step_id === nextStep.id);

            // Stop if no corresponding CampaignInstitution/CampaignPerson entry has been found
            if (!hasNextTarget) {
                break;
            }

            currentStep = nextStep;
        }

        return currentStep;
    }

    /**
     * Save or update a campaign.
     * @param campaign Partial Campaign update dto
     * @param reloadOnSuccess Reload campaigns after a successful save
     * @param rollbackId Optional campaign id to unhide if request fails
     */
    private updateOrSaveCampaign(
        campaign: Partial<Campaign>,
        reloadOnSuccess: boolean,
        rollbackId: number | null = null,
    ): void {
        const serviceRequest$ = this.campaignsService.saveOrUpdate(campaign);

        serviceRequest$.subscribe({
            next: () => {},
            error: (error) => {
                // eslint-disable-next-line no-console
                console.error(error);

                // on error, unhide the campaign if it was hidden
                if (rollbackId !== null) {
                    this.unhideForCurrentEntity(rollbackId);
                }
            },
            complete: () => {
                // Reload einzelner Kampagne damit die Änderungen direkt angezeigt werden
                this.campaignsService.requestCampaignRefresh(campaign.id);

                // Alle Kampagnen neu laden für aktuelle Daten in den Dashboards
                this.campaignsService.reloadAll();
                this.changeDetectorRef.markForCheck();
            },
        });
    }

    /**
     * Check if a status key is in the "final" status category
     * @param {string} statusKey
     */
    private isFinalStatus(statusKey: string): boolean {
        const matchingListentry = this.campaignStatusListentries?.find((listentry) => listentry.list_key === statusKey);
        const listData = JSON.parse(matchingListentry?.list_data ?? '{}');
        return notEmpty(matchingListentry) && listData?.category === this.STATUS_CATEGORY_FINAL;
    }

    /**
     * Get the next step from the current step
     * @param {CampaignTarget} target campaigns_institutions or campaigns_people
     */
    private getNextCampaignStepId(target: CampaignTarget): Id | null {
        const campaign = this.filteredCampaigns().find((c) => c.id === target.campaign_id);
        const currentStep = campaign?.campaign_steps.find((step) => step.id === target.campaign_step_id);

        if (currentStep) {
            const nextStep = campaign?.campaign_steps.find((step) => step.id === currentStep.next_step_id);
            return nextStep?.id ?? null;
        }

        return null;
    }

    /**
     * Get the status key of the first found listentry with the status category "start"
     */
    private getInitialStatusKey(): string | undefined {
        const startEntry = this.campaignStatusListentries?.find(
            (listentry) => listentry.list_data?.category === this.STATUS_CATEGORY_START,
        );

        return startEntry?.list_key;
    }

    /**
     * Set the animation state to apply an animation on a campaign card
     * @param {Id} campaignId
     * @param {'remove' | 'flip'} animationType
     * @param {boolean} newState
     */
    private setCampaignAnimationState(campaignId: Id, animationType: 'remove' | 'flip', newState: boolean) {
        const numericCampaignId = Number(campaignId);

        if (animationType === 'remove') {
            this.campaignRemoveAnimationStates.set(numericCampaignId, newState);
        }

        if (animationType === 'flip') {
            this.campaignFlipAnimationStates.set(numericCampaignId, newState);
        }
    }

    /**
     * Checks if a campaign is in the final step and final status
     * @param {Campaign} campaign
     */
    public isCampaignInFinalStepAndStatus(campaign: Campaign): boolean {
        const latestStep = this.getLatestCampaignStep(campaign);

        if (latestStep && latestStep.next_step_id === null) {
            const targets = getCampaignTargets(campaign, this.parentModule());
            const statusKey = targets.find((target) => target.campaign_step_id === latestStep.id)?.status_key;

            return statusKey !== undefined && this.isFinalStatus(statusKey);
        }

        return false;
    }

    // Load user data from the storage
    private loadUserData(): Promise<void> {
        const promise = this.storageService.getItem<User | null>('ownUser');
        return promise.then((userData) => {
            if (userData) {
                this.currentUser = userData;
            }
        });
    }

    /**
     * Checks whether the current user is allowed to edit a campaign
     * The user must have a role that has edit_right for at least one of the campaign's campaigns_roles
     * @param campaign The campaign to check
     */
    private hasEditPermission(campaign: Campaign): boolean {
        const roleIds = this.currentUser?.role_ids ?? [];

        if (!Array.isArray(roleIds) || roleIds.length === 0) {
            return false;
        }

        return campaign.campaigns_roles?.some((r) => r.edit_right === true && roleIds.includes(r.role_id)) ?? false;
    }

    // Hide campaign only for the current entity
    private hideForCurrentEntity(campaignId: number): void {
        const entityId = this.selectedEntityId();
        if (entityId === null) return;

        this.hiddenByEntity.update((prev) => {
            const next = new Map(prev);
            const set = new Set(next.get(entityId) ?? []);
            set.add(campaignId);
            next.set(entityId, set);
            return next;
        });
    }

    /*
     * Unhide campaign only for the current entity when a save/update request fails
     */
    private unhideForCurrentEntity(campaignId: number): void {
        const entityId = this.selectedEntityId();
        if (entityId === null) return;

        this.hiddenByEntity.update((prev) => {
            if (!prev.has(entityId)) return prev;
            const next = new Map(prev);
            const set = new Set(next.get(entityId)!);
            set.delete(campaignId);
            if (set.size) next.set(entityId, set);
            else next.delete(entityId);
            return next;
        });
    }
}
